import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintMri = makeArticle({
  slug: "does-saint-tms-require-an-mri",
  title:
    "Does SAINT\u00ae TMS Require an MRI? Yes. Here's Why the Scan Matters | Neuro Wellness",
  description:
    "Yes, SAINT\u00ae needs a brain MRI. Learn why head measurements can't find your personal target, what the scan involves, and how to verify it's real SAINT\u00ae.",
  canonical: "/learn/does-saint-tms-require-an-mri",
  h1: "Does SAINT\u00ae TMS Require an MRI? Yes. Here's Why the Scan Matters",
  breadcrumbLabel: "Does SAINT\u00ae Require an MRI?",
  shortAnswer:
    "Yes. Every SAINT\u00ae patient has a brain MRI before treatment. Software uses the scan's functional-connectivity data to pick the exact spot to stimulate for that person. A clinic that offers a 5-day course without a brain scan isn't delivering SAINT\u00ae.",
  keyTakeaways: [
    "Yes. SAINT\u00ae requires a structural MRI and a functional MRI (fMRI) before treatment.",
    "The scan maps brain connectivity to compute a personalized stimulation target.",
    "A 5-day accelerated course delivered without a brain MRI is not SAINT\u00ae.",
    "The scan is painless, non-invasive, and requires no contrast injection.",
    "People with non-removable magnetic-sensitive metal in the head or certain implants cannot have the MRI.",
  ],
  sections: [
    {
      heading: "Does SAINT TMS require an MRI?",
      paragraphs: [
        "Yes. Every SAINT\u00ae patient has a brain MRI before treatment. Software uses the scan's functional-connectivity data to pick the exact spot to stimulate for that person. A clinic that offers a 5-day course without a brain scan isn't delivering SAINT\u00ae.",
        "Conventional TMS places the coil using a scalp measurement \u2014 the distance from the spot that makes the thumb twitch. SAINT\u00ae replaces this ruler-based approach with a functional MRI that maps how each patient's brain is wired. The software identifies the specific location on the left dorsolateral prefrontal cortex that has the strongest functional connection to the subgenual anterior cingulate cortex, a deeper region involved in mood regulation.",
      ],
    },
    {
      heading: "Can SAINT be done without an fMRI?",
      paragraphs: [
        "No. Personalized MRI targeting is part of the FDA-cleared protocol. Without the scan, software cannot compute the individualized target, and the treatment cannot be SAINT\u00ae.",
        "Some clinics offer 5-day accelerated TMS programs that copy the 50-session schedule but skip the MRI scan, using scalp or head measurements instead. While these off-label programs exist, they are a different treatment and cannot claim the FDA clearance or trial results of SAINT\u00ae.",
      ],
    },
    {
      heading: "Is head-measurement targeting the same as SAINT targeting?",
      paragraphs: [
        "No. Head-measurement methods \u2014 such as measuring from the motor cortex or using scalp-based coordinates like the BEAM F3 method \u2014 rely on population averages and external landmarks.",
        "SAINT\u00ae targeting uses your actual functional MRI scan to locate your unique brain circuit. Because human brain anatomy and functional connectivity vary significantly between individuals, a scalp measurement cannot identify your specific subgenual-connected target. Learn more about [[fMRI-guided TMS vs scalp targeting|/learn/fmri-guided-tms-for-depression]].",
      ],
    },
    {
      heading: "What happens during the SAINT MRI?",
      paragraphs: [
        "The scan is painless, non-invasive, and uses no contrast dye or injections. You simply lie still in the MRI scanner for roughly 45 to 60 minutes while the structural and resting-state functional MRI images are gathered.",
        "The scan is a one-time requirement completed before your treatment week begins. It is not repeated during the five days of treatment. Once the algorithm processes your scan and computes your target, that exact coordinate is used for all 50 sessions during your week.",
      ],
    },
    {
      heading: "Who can't have the MRI?",
      paragraphs: [
        "Standard MRI safety screening applies before scheduling. People with non-removable ferromagnetic metal in or near the head \u2014 such as cochlear implants, aneurysm clips, or certain stents \u2014 or implanted electronic stimulators cannot safely have an MRI.",
        "Because the brain MRI is an essential requirement of the SAINT\u00ae protocol, individuals who cannot safely undergo an MRI are not candidates for SAINT\u00ae. Conventional TMS, which does not require an MRI, may still be an option.",
      ],
      links: [
        {
          to: "/am-i-a-candidate",
          text: "Check your eligibility for SAINT\u00ae",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Does SAINT TMS require an MRI?",
      answer:
        "Yes. Every SAINT\u00ae patient has a brain MRI before treatment. Software uses the scan's functional-connectivity data to pick the exact spot to stimulate for that person. A clinic that offers a 5-day course without a brain scan isn't delivering SAINT\u00ae.",
    },
    {
      question: "Can SAINT be done without an fMRI?",
      answer:
        "No. Personalized MRI targeting is part of the FDA-cleared protocol. Without the fMRI scan, software cannot compute the individualized target, and the treatment is not SAINT\u00ae.",
    },
    {
      question: "Is head-measurement targeting the same as SAINT targeting?",
      answer:
        "No. Head-measurement methods rely on scalp landmarks and population averages. SAINT\u00ae uses a functional MRI to map your individual brain circuitry.",
    },
    {
      question: "What happens during the SAINT MRI?",
      answer:
        "The MRI is painless, non-invasive, and requires no contrast injection. You lie still in the scanner for about 45 to 60 minutes before your treatment week. The scan is a one-time requirement.",
    },
    {
      question: "Who can't have the MRI?",
      answer:
        "People with non-removable magnetic-sensitive metal in or near the head, cochlear implants, aneurysm clips, or implanted electronic devices cannot safely have an MRI and are not candidates for SAINT\u00ae.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
  ],
  relatedLinks: saintQuestionRelated,
});
